import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getGiftAgentWorkspaceUrl,
  getProjectWorkflowUrl,
  getSessionOpenUrl,
  isPmCapableRunner,
  isTeamWorkflowRunner,
} from '@/lib/gift-agent';
import {
  getTemplateIconClassName,
  getTemplateIconComponent,
  getTemplateIconStyleAttribute,
} from '@/lib/template-catalog';
import type {
  Project,
  Session,
  ProjectTemplate,
} from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { StatusIndicator } from '@/components/ui/status-indicator';
import {
  Play,
  Database,
  FolderGit2,
  File,
  GitBranch,
  Users,
  Workflow,
} from 'lucide-react';

interface ProjectCardProps {
  project: Project;
  activeSession?: Session | null;
  template?: ProjectTemplate | null;
  startingSession?: string | null;
  onStart: (projectId: string) => void;
}

export function ProjectCard({
  project,
  activeSession,
  template,
  startingSession,
  onStart,
}: ProjectCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on a button or link inside the card footer
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    navigate(`/projects/${project.id}`);
  };

  const TemplateIcon = getTemplateIconComponent(template);

  return (
    <Card
      className="bg-card border rounded-lg overflow-hidden flex flex-col hover:bg-accent/50 transition-colors duration-150 cursor-pointer"
      onClick={handleCardClick}
    >
      <CardContent className="p-5 flex-1">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                template
                  ? getTemplateIconClassName(template)
                  : project.gitUrl
                    ? 'bg-foreground text-background'
                    : 'bg-muted text-muted-foreground'
              }`}
              style={
                template ? getTemplateIconStyleAttribute(template) : undefined
              }
            >
              {project.gitUrl ? (
                <FolderGit2 className="w-5 h-5" />
              ) : template ? (
                <TemplateIcon className="w-5 h-5" />
              ) : (
                <File className="w-5 h-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{project.name}</h3>
              <p className="text-xs text-muted-foreground">
                {project.isShared && project.ownerName
                  ? t('projectCard.sharedBy', { ownerName: project.ownerName })
                  : t('projectCard.createdOn', {
                      date: new Intl.DateTimeFormat(
                        i18n.resolvedLanguage || i18n.language,
                        {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        },
                      ).format(new Date(project.createdAt)),
                    })}
              </p>
            </div>
          </div>
          {activeSession && <StatusIndicator status="running" size="lg" />}
        </div>

        {/* Project Meta */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.hasDatabase && (
            <Badge
              variant="secondary"
              className="gap-1 bg-success/10 text-success hover:bg-success/15"
            >
              <Database className="w-3 h-3" />
              {t('projectCard.postgresql')}
            </Badge>
          )}
          {template && (
            <Badge
              variant="secondary"
              className="bg-primary/10 text-primary hover:bg-primary/20"
            >
              {template.name}
            </Badge>
          )}
          {project.gitUrl && (
            <Badge variant="secondary" className="gap-1">
              <FolderGit2 className="w-3 h-3" />
              {t('projectCard.github')}
            </Badge>
          )}
          {project.hasForgejoRepo && (
            <Badge
              variant="secondary"
              className="gap-1 bg-warning/10 text-warning hover:bg-warning/15"
            >
              <GitBranch className="w-3 h-3" />
              {t('projectCard.forgejo')}
            </Badge>
          )}
          {project.isShared && (
            <Badge
              variant="secondary"
              className="gap-1 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
            >
              <Users className="w-3 h-3" />
              {t('projectCard.shared')}
            </Badge>
          )}
        </div>

        {/* Git URL */}
        {project.gitUrl && (
          <p
            className="text-xs text-muted-foreground/70 truncate mb-3"
            title={project.gitUrl}
          >
            {project.gitUrl.replace('https://github.com/', '')}
          </p>
        )}
      </CardContent>

      {/* Project Actions */}
      <CardFooter className="px-5 py-3 bg-muted/30 border-t flex items-center justify-end">
        {activeSession ? (
          isTeamWorkflowRunner(activeSession.runnerType) ? (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="text-success border-success/20 bg-success/10 hover:bg-success/15"
            >
              <a
                href={getProjectWorkflowUrl(project.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(getProjectWorkflowUrl(project.id));
                }}
              >
                <span className="w-2 h-2 bg-success rounded-full mr-2 animate-pulse" />
                {t('projectCard.openWorkflow')}
              </a>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              size="sm"
              className="text-success border-success/20 bg-success/10 hover:bg-success/15"
            >
              <a
                href={
                  isPmCapableRunner(activeSession.runnerType)
                    ? getGiftAgentWorkspaceUrl(activeSession.id)
                    : getSessionOpenUrl(activeSession.id)
                }
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/sessions/${activeSession.id}`);
                }}
              >
                <span className="w-2 h-2 bg-success rounded-full mr-2 animate-pulse" />
                {t('projectCard.openSession')}
              </a>
            </Button>
          )
        ) : (
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onStart(project.id);
            }}
            disabled={startingSession === project.id}
            className="gap-1.5"
          >
            {startingSession === project.id ? (
              <>
                <Spinner className="h-3 w-3" />
                {project.isTeamMode
                  ? t('projectCard.openingWorkflow')
                  : t('projectCard.starting')}
              </>
            ) : (
              <>
                {project.isTeamMode ? (
                  <Workflow className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {project.isTeamMode
                  ? t('projectCard.openWorkflow')
                  : t('projectCard.start')}
              </>
            )}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
